// cSpell: words mapboxgl saleticket mapbox gmap
import mapboxgl from "mapbox-gl";
import {
  getSaleTicket,
  getSpot,
  listAllSpots,
  listAllTags,
} from "../global-commons/apiUtil";
import {
  Languages,
  MAP_SUPPORTED_LANGUAGES,
} from "../global-commons/i18n/base";
import { Spot } from "../global-commons/type";
import { DATA_BLOCK } from "./dataBlockUtil";
import { convertSpotsToGeoJson, setPropertyToFeature } from "./geoJsonUtil";
import {
  DEFAULT_LAT,
  DEFAULT_LON,
  DEFAULT_MARKER_SIZE,
  DEFAULT_ZOOM,
  DELAYED_SEARCH_MILLISECONDS,
  DRAWER_WIDTH,
  EMPHASIZED_MARKER_SIZE,
  getMobileDrawerInitialTop,
  getMobileDrawerThresholdTop,
  MAP_DEFAULT_PADDING,
  MARKER_DEBUG,
  MOBILE_DRAWER_ENLARGED_TOP,
  MOBILE_SEARCH_BUTTON_HEIGHT,
  MOBILE_WIDTH,
  Mode,
  UPPER_NAVBAR_HEIGHT,
} from "./gmapCommon";
import {
  closeFilterDrawer,
  closeSaleTicketDetailDrawer,
  locateFilterDrawer,
  locateSaleTicketDetailDrawer,
  openFilterDrawer,
  openSaleTicketDetailDrawer,
} from "./gmapSaleTickets";
import {
  closeSpotDetailDrawer,
  loadAvailableSaleTickets,
  locateSpotDetailDrawer,
  openSpotDetailDrawer,
  setIsSaleTicketSearched,
} from "./gmapSpots";
import {
  filterSpotsByKeywords,
  registerSearchHandlers,
  splitSearchKeywords,
  upsertSearchNoResultsMessage,
} from "./gmapSearch";
import { getSpotWithSaleTickets } from "./searchApiUtil";

let _map: mapboxgl.Map | null = null;
let _geolocate: mapboxgl.GeolocateControl | null = null;
// @ts-expect-error MAPBOX_API_KEYは、HTMLのscriptタグで定義されている
mapboxgl.accessToken = MAPBOX_API_KEY;

// ドロワーメニューのリサイズの状態を表す変数群

/** スクロール状態?
 * "none" -> スクロールしていない
 * "normal" -> スクロールしている。この時、ドロワーのtop値は、
 * ドロワーのコンテンツのscrollTopの値で保障されて
 * "forced" -> ドロワーのハンドルをドラッグして強制的に位置を変更している。
 * この時、ドロワーコンテンツのscrollTopの値の補償は行わない。つまり、
 * ドロワーのコンテンツはスクロールされたまま。
 */
let scrollStatus: "none" | "normal" | "forced" = "none";
/** マウスの押下を開始したときのevent.pageYの値 */
let mouseStartPageY: number = 0;
/** マウスの押下開始してからの移動距離 */
let mouseDiffPageY: number = 0;
/** マウス押下開始時のSpotDetailDrawerのtopの値 */
let spotDetailDrawerInitialTop: number = 0;
/** マウス押下開始時のsaleTicketDetailDrawerのtopの値 */
let saleTicketDetailDrawerInitialTop: number = 0;
/** マウス押下開始時のspot-detail-drawer-contentのscrollTopの値 */
let spotDetailDrawerContentInitialScrollTop: number = 0;
/** マウス押下開始時のsale-ticket-detail-drawer-contentのscrollTopの値 */
let saleTicketDetailDrawerContentInitialScrollTop: number = 0;
/** ドロワーの移動速度　ドロワーの慣性の計算に使用 */
let drawerMoveSpeed: number = 0;
/** 前イベントコール時のドロワーのtopの値 */
let previousDrawerTop: number = 0;

/** 検索0件メッセージ要素のID */
const SEARCH_NO_RESULTS_MESSAGE_ID = "map-search-no-results-message";

/** 現在有効になっているカテゴリタグのid, -1なら全タグを選択 */
let enabledCategoryTag: number = -1;

// マーカー管理オブジェクト
// 参考: https://docs.mapbox.com/mapbox-gl-js/ja/example/cluster-html/
/** マップ上に存在する(可視・非可視両方含む)全マーカーのインスタンス */
let markers: { [key: string]: mapboxgl.Marker } = {};
/** 現在画面上に表示されているマーカーのインスタンス */
let markersOnScreen: { [key: string]: mapboxgl.Marker } = {};

async function init() {
  const mode = getMode();
  showLoading();
  try {
    await createMap();
    // マップの初期化後、ソースをロードする
    _map!.on("load", async () => {
      const spots = await changeDrawedMarkers();
      if (mode.mode === "singleSpot") {
        const targetSpot = spots.find((spot) => spot.topics_id === mode.id);
        if (!targetSpot) {
          console.error(`Spot with id ${mode.id} not found`);
          return;
        }
        zoomToSpots([targetSpot]);
      } else {
        zoomToSpots(spots);
      }
      loadDummyLayer();
    });
    registerCallbacks();
    registerResizeObserver();
    registerDrawerPullLogics();
    changeUiVisibility(mode);
    await toggleDrawer(mode);
    await addTagsToLowerNavbar();
  } finally {
    hideLoading();
  }
}

/** URLから現在の動作モードを決定する
 * @see {@link mode}
 */
function getMode(): Mode {
  const params = new URLSearchParams(document.location.search);
  const spot = params.get("spot");
  if (spot) {
    return {
      mode: "singleSpot",
      id: parseInt(spot, 10),
    };
  }
  const saleTicket = params.get("saleticket");
  if (saleTicket) {
    return {
      mode: "saleTicket",
      id: parseInt(saleTicket, 10),
    };
  }
  return {
    mode: "spots",
    id: null,
  };
}

async function createMap() {
  const location = window.location;
  const lang = location.pathname.split("/")[1];
  const mapLang = MAP_SUPPORTED_LANGUAGES[lang as Languages] || "en";
  _map = new mapboxgl.Map({
    container: "map",
    style: DATA_BLOCK.getMapStyleUrl(),
    center: [DEFAULT_LON, DEFAULT_LAT],
    zoom: DEFAULT_ZOOM,
    language: mapLang,
    logoPosition: "top-right",
  });

  // Geolocationコントローラーを生成する
  // ただし、オリジナルのUIの下に配置して見えないようにする。
  _geolocate = new mapboxgl.GeolocateControl({
    positionOptions: {
      enableHighAccuracy: true,
    },
    trackUserLocation: true,
    showUserHeading: true,
  });

  _map.on("render", () => {
    if (!_map) return;
    if (!_map.isSourceLoaded("spots")) return;

    updateMarkers();
  });

  _map.addControl(_geolocate, "bottom-right");
}

/** フィーチャー`spots`を元に、クラスタ化されたスポットマーカの描画更新を行う
 * この関数は、マップの`render`イベントで呼び出され、レンダリングループ毎に実行される
 */
function updateMarkers() {
  if (!_map) return;

  const newMarkers: { [key: string]: mapboxgl.Marker } = {};
  const features = _map.querySourceFeatures("spots");

  for (const feature of features) {
    if (feature.type !== "Feature") continue;
    // @ts-expect-error why?
    const coords = feature.geometry.coordinates;
    const props = feature.properties;
    const isCluster = !!props!.cluster;
    const id = isCluster ? props!.cluster_id : props!.uniqueId;

    let marker = markers[id];
    let isNewMarker = false;
    if (!marker) {
      isNewMarker = true;
      const label = isCluster ? "" : props!.subject;
      const debugLabel = isCluster ? props!.topicsIds : props!.topicsId;
      const el = createMarkerElement(
        "white",
        isCluster ? props!.point_count : props!.markerImageUrl,
        MARKER_DEBUG ? debugLabel : label,
        props!.isSelected === "true",
      );
      // addTo直後の一瞬だけ(0,0)に描画されるチラつきを防ぐ
      el.style.opacity = "0";
      if (isCluster) {
        el.addEventListener("click", () => {
          // クラスタをクリックしたときは、センタライズして1段階ズームする
          _map!.flyTo({
            center: coords,
            zoom: _map!.getZoom() + 1,
            essential: true, // アニメーションを必須にする
          });
        });
      } else {
        el.addEventListener("click", () => {
          changeMode(
            {
              mode: "singleSpot",
              id: props!.topicsId,
            },
            "noZoomAndCenterize",
          );
        });
      }
      marker = markers[id] = new mapboxgl.Marker({ element: el }).setLngLat(
        coords,
      );

      console.debug(
        `new marker ${id}(${isCluster ? "cluster" : "spot"},${
          isCluster ? props!.topicsIds : props!.topicsId
        }) added to markers[]`,
      );
    } else {
      marker.setLngLat(coords);
    }
    newMarkers[id] = marker;

    if (!markersOnScreen[id]) {
      marker.addTo(_map);
      if (isNewMarker) {
        const markerElement = marker.getElement();
        window.requestAnimationFrame(() => {
          markerElement.style.opacity = "1";
        });
      }
      console.debug(`marker ${id} added to map`);
    }
  }

  for (const id in markersOnScreen) {
    if (!newMarkers[id]) {
      markersOnScreen[id].remove();
      console.debug(`marker ${id} removed from map`);
    }
  }
  markersOnScreen = newMarkers;
}

/** 現在描画しているマーカを全てクリアして、新しいSpotをロードできる状態にする */
function clearAllMarkers() {
  // sourceを更新したばあい、異なるclusterにおなじIDが使用されて
  // 本来表示したくないclusterスポットを表示することがある。
  // それを防ぐためにすでに存在するマーカーをすべて削除する
  for (const id in markers) {
    // markersには、描画している・していないに関わらず、すべてのマーカーのインスタンスが格納している
    // markersを単純に空にしてしまうと、マップ上に存在するのに、インスタンスが存在せず操作できないスポットが出来上がる
    // それを防ぐために全スポットをマップから削除する
    markers[id].remove();
    console.debug(`marker ${id} removed from map`);
  }
  markers = {};
  // 全マーカはマップから削除されたので、markersOnScreenを空にする
  markersOnScreen = {};
}

function registerCallbacks() {
  // ナブバーの言語切り替えスイッチの処理
  const languageSelection = document.getElementById(
    "navbar-language-switcher-selection",
  );
  if (!languageSelection) {
    throw new Error("navbar-language-switcher-selection not found");
  }
  languageSelection.addEventListener("click", (e: Event) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "A") {
      const lang = target.getAttribute("data-lang");
      if (lang) {
        const currentLocation = window.location;
        // 言語パスのみを変更して、mapページにリダイレクトする
        window.location.href = `/${lang}/map${currentLocation.search}`;
      }
    }
  });
  // スポット詳細ドロワーの閉じるボタン
  const spotDetailDrawerCloseButton = document.getElementById(
    "spot-detail-drawer-close-button",
  );

  if (!spotDetailDrawerCloseButton) {
    throw new Error("spotDetailDrawerCloseButton not found");
  }

  spotDetailDrawerCloseButton.addEventListener("click", (e: Event) => {
    changeMode(
      {
        mode: "spots",
        id: null,
      },
      "noZoomAndCenterize",
    );
  });

  // 販売チケット詳細ドロワーの閉じるボタン
  const saleTicketDetailDrawerCloseButton = document.getElementById(
    "sale-ticket-detail-drawer-close-button",
  );

  if (!saleTicketDetailDrawerCloseButton) {
    throw new Error("saleTicketDetailDrawerCloseButton not found");
  }

  saleTicketDetailDrawerCloseButton.addEventListener("click", (e: Event) => {
    changeMode(
      {
        mode: "spots",
        id: null,
      },
      "noZoomAndCenterize",
    );
  });

  // ブラウザ戻る/進むボタン押下時に、モードを変更する
  window.addEventListener("popstate", () => {
    const mode = getMode();
    // ブラウザの戻るボタンを押したときは、スポット詳細ドロワーで販売チケットが検索されたことにする
    setIsSaleTicketSearched(true);
    changeMode(mode, "normal");
  });
  // 地図の3d/2d切り替えボタン押下時に、地図の目線を変更する
  const from3dTo2dChanger = document.getElementById("3d-2d-changer");
  if (!from3dTo2dChanger) {
    throw new Error("3d-2d-changer not found");
  }
  from3dTo2dChanger.addEventListener("click", toggleMap3d2d);
  // ユーザー位置情報取得ボタン押下時に、ユーザー位置情報を取得する
  const showCurrentPosButton = document.getElementById(
    "show-current-position-button",
  );
  if (!showCurrentPosButton) {
    throw new Error("show-current-position-button not found");
  }
  showCurrentPosButton.addEventListener("click", showCurrentPos);
  // ホームポジションボタン押下時に、ホームポジションに戻る
  const homePositionButton = document.getElementById("home-position-button");
  if (!homePositionButton) {
    throw new Error("home-position-button not found");
  }
  homePositionButton.addEventListener("click", async () => {
    const spots = await changeDrawedMarkers();
    zoomToSpots(spots);
  });
  // 検索欄入力時、内容をフィルタドロワーのsearchBoxに同期する
  const searchBox = document.getElementById(
    "search-box",
  ) as HTMLInputElement | null;
  if (!searchBox) {
    throw new Error("search-box not found");
  }
  const searchClearButton = document.getElementById(
    "search-clear-button",
  ) as HTMLButtonElement | null;
  if (!searchClearButton) {
    throw new Error("search-clear-button not found");
  }
  // 検索入力UI(IME/デバウンス/クリアボタン)は専用モジュールに集約する。
  // gmap.ts 側は「検索時に必要な副作用(モード解除・再描画・ズーム)」のみ提供する。
  registerSearchHandlers({
    delayedSearchMilliseconds: DELAYED_SEARCH_MILLISECONDS,
    searchBox,
    searchClearButton,
    onSearch: async () => {
      const mode = getMode();
      if (mode.mode === "singleSpot") {
        const url = new URL(window.location.href);
        url.searchParams.delete("spot");
        // 検索入力のたびに履歴を増やさないよう、replaceStateで現在URLを更新する
        window.history.replaceState({}, "", url.toString());
        closeSpotDetailDrawer();
      }

      // 検索入力中はオーバーレイ表示を抑止してちらつきを防ぐ
      const spots = await changeDrawedMarkers(false);
      // 検索でヒットしたスポットへカメラ移動し、見落としを防ぐ
      zoomToSpots(spots, true);
    },
  });
  // チケットボタンを押してフィルタ条件ドロワーを開く
  const ticketButton = document.getElementById("ticket-button");
  if (!ticketButton) {
    throw new Error("ticket-button not found");
  }
  ticketButton.addEventListener("click", () => {
    openFilterDrawer();
  });
  // 閉じるボタンでフィルタ条件ドロワーを閉じる
  const filterDrawerHandleCloseButton = document.getElementById(
    "filter-drawer-handle-close-button",
  );
  if (!filterDrawerHandleCloseButton) {
    throw new Error("filter-drawer-handle-close-button not found");
  }
  filterDrawerHandleCloseButton.addEventListener("click", closeFilterDrawer);
  // フィルタ状態ドロワーのチケット検索条件セレクトボックスを変更したとき、
  // フィルタドロワーを再描画する
  const ticketConditionSelector = document.getElementById(
    "ticket-condition-selector",
  ) as HTMLSelectElement | null;
  if (!ticketConditionSelector) {
    throw new Error("ticket-condition-selector not found");
  }
  ticketConditionSelector.addEventListener("change", () => {
    openFilterDrawer();
  });
  // スポット詳細ドロワーの「使用できるチケット」ボタンの処理
  const spotDetailDrawerLoadSaleTicketButton = document.getElementById(
    "spot-detail-drawer-load-sale-ticket-button",
  );
  if (!spotDetailDrawerLoadSaleTicketButton) {
    throw new Error("spot-detail-drawer-load-sale-ticket-button not found");
  }
  spotDetailDrawerLoadSaleTicketButton.addEventListener("click", () => {
    loadAvailableSaleTickets();
    setIsSaleTicketSearched(true);
  });
  window.addEventListener("changeModeToSaleTicket", ((e: CustomEvent) => {
    changeMode({
      mode: "saleTicket",
      id: e.detail.id,
    });
  }) as EventListener);
}

function showLoading() {
  const loading = document.getElementById("loading-background");
  if (loading) {
    loading.style.display = "flex";
  }
}

function hideLoading() {
  const loading = document.getElementById("loading-background");
  if (loading) {
    loading.style.display = "none";
  }
}

async function addTagsToLowerNavbar() {
  const tagsContainer = document.getElementById(
    "nav-lower-layer-tag-container",
  );
  if (!tagsContainer) {
    throw new Error("nav-lower-layer-tag-container not found");
  }
  tagsContainer.innerHTML = "";

  // 全てタグを最初に追加する
  // 全てタグは、全てのカテゴリタグの有効・無効を切り替える
  const [allTagsInputElement, allTagsLabelElement] =
    createCategoryTagSelectorElement(
      "all",
      DATA_BLOCK.getI18n()["map.categoryAll"],
      true, // デフォルトで"全て"はチェックされている
      "tag-interest-navbar",
      undefined,
      async (e: Event) => {
        const target = e.target as HTMLInputElement;
        const isChecked = target.checked;
        // 全てタグのチェックボックスがONなら、全てのカテゴリタグを有効にする
        // OFFなら、全てのカテゴリタグを無効にする
        if (isChecked) {
          // カテゴリタグ"全て"を有効にする
          enabledCategoryTag = -1;
        }
        const currentMode = getMode();
        changeMode({ id: null, mode: "spots" }, "noZoomAndCenterize");
        // 下層navbarのタグを全て描画する
        const spots = await changeDrawedMarkers();
        zoomToSpots(spots);
      },
    );

  const wrapperDiv = document.createElement("div");
  wrapperDiv.appendChild(allTagsInputElement);
  wrapperDiv.appendChild(allTagsLabelElement);
  tagsContainer.appendChild(wrapperDiv);

  // カテゴリを取得してタグを追加する
  const lang = location.pathname.split("/")[1];
  const categories = await listAllTags(
    DATA_BLOCK.getEnv().API_URL,
    { categoryIds: [DATA_BLOCK.getSelectableTagCategoryInMap()] },
    lang as Languages,
  );

  categories.forEach((category) => {
    const isChecked = category.tag_id === enabledCategoryTag;
    const [inputTag, categoryLabel] = createCategoryTagSelectorElement(
      category.tag_id.toString(),
      category.tag_nm.replace(/_/g, "\u00A0"),
      isChecked,
      "tag-interest-navbar",
      undefined,
      async (e: Event) => {
        const target = e.target as HTMLInputElement;
        const isChecked = target.checked;
        if (isChecked) {
          // チェックが入った場合、enabledCategoryTagに設定する
          enabledCategoryTag = category.tag_id;
        }
        changeMode({ id: null, mode: "spots" }, "normal");
      },
    );

    const wrapperDiv = document.createElement("div");
    wrapperDiv.appendChild(inputTag);
    wrapperDiv.appendChild(categoryLabel);
    tagsContainer.appendChild(wrapperDiv);
  });

  // @ts-expect-error jqueryはHTML上で定義されている
  $("#nav-lower-layer-tag-container").slick({
    variableWidth: true,
    infinite: false,
    arrows: true,
    prevArrow: `<button type="button" class="tag-carousel-arrow override-color"><</button>`,
    nextArrow: `<button type="button" class="tag-carousel-arrow override-color">></button>`,
  });
}
/** タグ要素を作成する */
function createCategoryTagSelectorElement(
  categoryId: string,
  tagName: string,
  checked: boolean,
  elementIdPrefix: string,
  additionalClasses: string[] = [],
  onclickCallback: ((e: Event) => void) | ((e: Event) => Promise<void>) | null,
): [HTMLInputElement, HTMLLabelElement] {
  const inputTag = document.createElement("input");
  inputTag.type = "radio";
  // デフォルトでチェック済み
  inputTag.checked = true;
  inputTag.classList.add(
    "filter-toggle-input",
    "override-color-checked",
    "override-border-color-checked",
    ...additionalClasses,
  );
  inputTag.id = elementIdPrefix + "-" + categoryId;
  inputTag.name = elementIdPrefix + "-category";
  inputTag.setAttribute("data-interest-topics-id", categoryId);
  inputTag.autocomplete = "off";
  inputTag.checked = checked;

  const categoryLabel = document.createElement("label");
  categoryLabel.classList.add("filter-toggle-label");
  categoryLabel.setAttribute("for", elementIdPrefix + "-" + categoryId);
  categoryLabel.innerText = tagName;

  if (onclickCallback) {
    inputTag.addEventListener("click", onclickCallback);
  }

  return [inputTag, categoryLabel];
}

/** フィルタ条件・検索の変更を認識して描画されるマーカーを変更する
 */
async function changeDrawedMarkers(
  showOverlay: boolean = true,
): Promise<Spot[]> {
  let spots: Spot[] = [];
  if (showOverlay) {
    showLoading();
  }
  try {
    const searchBox = document.getElementById(
      "search-box",
    ) as HTMLInputElement | null;
    if (!searchBox) {
      throw new Error("search-box not found");
    }

    const searchKeyWords: string[] = splitSearchKeywords(searchBox.value);
    const params = new URLSearchParams(window.location.search);
    const selectedSpotId = params.get("spot")
      ? parseInt(params.get("spot")!)
      : null;
    spots = await loadSpots(
      getMode(),
      enabledCategoryTag === -1 ? undefined : [enabledCategoryTag],
      searchKeyWords,
      selectedSpotId,
    );
    // 0件メッセージは検索語の有無を含めて一元判定する。
    upsertSearchNoResultsMessage(
      spots.length,
      searchBox.value,
      DATA_BLOCK.getI18n()["map.noSearchResults"] || "検索結果がありません",
      SEARCH_NO_RESULTS_MESSAGE_ID,
    );
  } finally {
    if (showOverlay) {
      hideLoading();
    }
  }
  return spots;
}
// 以上検索関連ロジック

/**
 * モードに応じてMapboxGLにロードされるスポットを変更する。
 * 基本的にこの関数を呼ばずに、changeDrawedMarkers()を呼ぶこと。
 * @returns 実際に描画したスポットのリスト
 */
async function loadSpots(
  mode: Mode,
  filterInterestTopicsIds?: number[],
  searchKeyWords?: string[],
  selectedSpotId: number | null = null,
): Promise<Spot[]> {
  const location = window.location;
  const lang = location.pathname.split("/")[1];
  if (mode.mode === "spots" || mode.mode === "singleSpot") {
    // 全スポットをロードする。
    let spots: Spot[] = [];
    if (filterInterestTopicsIds && filterInterestTopicsIds.length === 0) {
      // filterInterestTopicsIdsに空配列が渡された場合(nullではない),
      // 該当するスポットはないので、表示するスポットはない
      spots = [];
    } else {
      spots = await listAllSpots(
        DATA_BLOCK.getEnv().API_URL,
        {
          tagIds: filterInterestTopicsIds,
        },
        lang as Languages,
      );
      spots = filterSpotsByKeywords(spots, searchKeyWords);
    }
    const geojson = convertSpotsToGeoJson(spots);
    if (selectedSpotId) {
      setPropertyToFeature(geojson, selectedSpotId, "isSelected", "true");
    }
    if (_map?.isSourceLoaded("spots")) {
      // すでにスポットのソースが存在する場合は、削除する
      unloadDummyLayer();
      _map!.removeSource("spots");
    }
    _map!.addSource("spots", {
      type: "geojson",
      data: geojson,
      cluster: true,
      clusterProperties: {
        topicsIds: [
          "concat",
          ["concat", ["to-string", ["get", "topicsId"]], ","],
        ],
      },
    });
    loadDummyLayer();
    clearAllMarkers();
    return spots;
  } else if (mode.mode === "saleTicket") {
    // 単一の販売チケットに属するスポットのみを描画する
    const modeId = mode.id;
    if (modeId === null) {
      throw Error("unexpectedly mode.id is null");
    }
    const location = window.location;
    const lang = location.pathname.split("/")[1];
    const spotWithSaleTickets = await getSpotWithSaleTickets(
      modeId,
      DATA_BLOCK.getEnv().SEARCH_API_URL,
      lang as Languages,
    );
    if (!spotWithSaleTickets) {
      throw Error("saleTicket not found");
    }
    const duplicatedSpots = spotWithSaleTickets
      .map((ticket) => ticket.spots)
      .flat();
    const spots = uniquifiesSpotArray(duplicatedSpots);
    const geojson = convertSpotsToGeoJson(spots);
    if (_map!.isSourceLoaded("spots")) {
      // すでにスポットのソースが存在する場合は、削除する
      unloadDummyLayer();
      _map!.removeSource("spots");
    }
    _map!.addSource("spots", {
      type: "geojson",
      data: geojson,
      cluster: true,
      clusterProperties: {
        topicsIds: [
          "concat",
          ["concat", ["to-string", ["get", "topicsId"]], ","],
        ],
      },
    });
    loadDummyLayer();

    clearAllMarkers();
    return spots;
  } else {
    throw Error("unexpected mode: " + mode.mode);
  }
}

function zoomToSpots(spots: Spot[], noZoom: boolean = false) {
  // パディングの計算
  let padding: { bottom: number; top: number; right: number; left: number };
  if (getCurrentDevice() === "MOBILE") {
    const mode = getMode();
    if (mode.mode === "singleSpot" || mode.mode === "saleTicket") {
      // モバイルの場合、上部のnavbarの高さを考慮する
      padding = {
        top: UPPER_NAVBAR_HEIGHT + MAP_DEFAULT_PADDING,
        // iOSの可変UIで表示領域が変わっても、現在の実表示高さに合わせた
        // ドロワー初期位置を使って下側パディングを決める。
        bottom: getMobileDrawerInitialTop(),
        left: MAP_DEFAULT_PADDING,
        right: MAP_DEFAULT_PADDING,
      };
    } else {
      // モードがspotsの場合、drawerメニューは開いてないので、全体を表示する
      padding = {
        top: MAP_DEFAULT_PADDING,
        bottom: MAP_DEFAULT_PADDING + MOBILE_SEARCH_BUTTON_HEIGHT,
        left: MAP_DEFAULT_PADDING,
        right: MAP_DEFAULT_PADDING,
      };
    }
  } else {
    // PCの場合、特に考慮しない
    padding = {
      top: MAP_DEFAULT_PADDING,
      bottom: MAP_DEFAULT_PADDING,
      left: DRAWER_WIDTH + 100, // ドロワーが開いた状態でもマーカーが隠れないように
      right: MAP_DEFAULT_PADDING,
    };
  }

  if (spots.length === 0) {
    return;
  } else if (spots.length === 1) {
    // 単一スポットの時はそのスポットにズーム
    const lat = parseFloat(spots[0].place.gmap_y);
    const lng = parseFloat(spots[0].place.gmap_x);
    if (isNaN(lat) || isNaN(lng)) {
      throw new Error(
        "invalid latitude or longitude for spot:" + spots[0].topics_id,
      );
    }
    _map?.easeTo({
      center: [lng, lat],
      zoom: noZoom ? _map.getZoom() : 20,
      padding,
    });
    return;
  }
  const bounds = new mapboxgl.LngLatBounds();
  spots.forEach((spot) => {
    const lat = parseFloat(spot.place.gmap_y);
    const lng = parseFloat(spot.place.gmap_x);
    if (isNaN(lat) || isNaN(lng)) {
      throw new Error(
        "invalid latitude or longitude for spot:" + spot.topics_id,
      );
    }
    bounds.extend({ lat, lng });
  });
  _map?.fitBounds(bounds, { padding });
}

function loadDummyLayer() {
  // ダミーのレイヤーを追加して、sourceの内容をクエリできるようにする
  _map!.addLayer({
    id: "spots-layer",
    type: "circle",
    source: "spots",
    paint: {
      "circle-radius": 10,
      "circle-color": "#000000",
      "circle-opacity": 0,
    },
  });
}

function unloadDummyLayer() {
  // ダミーのレイヤーを削除する
  if (_map!.getLayer("spots-layer")) {
    _map!.removeLayer("spots-layer");
  }
}

function changeUiVisibility(mode: Mode) {}

/** window.bodyのリサイズを監視するオブザーバーを有効化する
 *
 * このオブザーバーは、以下の目的でwindow.bodyのリサイズを監視する
 * - ウィンドウのリサイズ時に、drawerクラスをもつ要素の幅(`width`)を変更
 * - navbarのcollapse時に、全navbarの高さに合わせて地図の高さを
 * 全画面に収まるように変更する
 */
function registerResizeObserver() {
  const observer = new ResizeObserver(() => {
    locateFilterDrawer();
    locateSpotDetailDrawer();
    locateSaleTicketDetailDrawer();
  });
  observer.observe(document.body);
  // 地図・スポット詳細ドロワーのコンテンツのリサイズ
  const mapContainer = document.getElementById("map");
  if (!mapContainer) {
    throw new Error("mapContainer not found");
  }
  const navbarUpper = document.getElementById("nav-upper-layer");
  if (!navbarUpper) {
    throw new Error("navbarUpper not found");
  }
  const navLayerWrapper = document.getElementById("nav-layer-wrapper");
  if (!navLayerWrapper) {
    throw new Error("nav-layer-wrapper not found");
  }

  // キーボードによる一時的なviewport縮小では、地図サイズを追従させない。
  // 追従すると検索中のfitBoundsが過度にズームアウトしやすくなる。
  let stableViewportHeight =
    window.visualViewport?.height ?? window.innerHeight;

  const shouldSkipMapResizeByKeyboard = (viewportHeight: number): boolean => {
    if (getCurrentDevice() !== "MOBILE") {
      return false;
    }

    const activeElement = document.activeElement;
    const isTextInputFocused =
      activeElement instanceof HTMLInputElement ||
      activeElement instanceof HTMLTextAreaElement ||
      (activeElement instanceof HTMLElement && activeElement.isContentEditable);
    if (!isTextInputFocused) {
      return false;
    }

    // 安定時より十分小さければ、仮想キーボード表示中とみなす。
    return viewportHeight < stableViewportHeight - 120;
  };

  const applyMapHeight = () => {
    // マップの高さを現在の表示領域とnavbarの高さから再計算する
    const navbarUpper = document.getElementById("nav-upper-layer");
    if (!navbarUpper) {
      throw new Error("navbarUpper not found");
    }
    const navLayerWrapper = document.getElementById("nav-layer-wrapper");
    if (!navLayerWrapper) {
      throw new Error("nav-layer-wrapper not found");
    }

    // visualViewport基準で再計算することで、iOS Safariの
    // アドレスバー開閉や自動拡大後でも map の実高さと整合させる。
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    if (shouldSkipMapResizeByKeyboard(viewportHeight)) {
      return;
    }
    stableViewportHeight = viewportHeight;

    const navHeight = navbarUpper.offsetHeight + navLayerWrapper.offsetHeight;
    mapContainer.style.height = `${Math.max(viewportHeight - navHeight, 0)}px`;
  };

  const navbarObserver = new ResizeObserver(applyMapHeight);
  navbarObserver.observe(navbarUpper);
  navbarObserver.observe(navLayerWrapper);
  // UI高さ変動の入口が複数あるため、DOMサイズ監視とviewport resizeの両方で追従する。
  window.addEventListener("resize", applyMapHeight);
  window.visualViewport?.addEventListener("resize", applyMapHeight);
  applyMapHeight();
}

/* 
ドロワーメニューの引っ張りロジックをHTMLに追加する
参考: https://codepen.io/hendrysadrak/pen/emdJpr
 */
function registerDrawerPullLogics() {
  const spotDetailDrawer = document.getElementById("spot-detail-drawer");

  if (!spotDetailDrawer) {
    throw new Error("spot-detail-drawer not found");
  }

  const saleTicketDetailDrawer = document.getElementById(
    "sale-ticket-detail-drawer",
  );

  if (!saleTicketDetailDrawer) {
    throw new Error("sale-ticket-detail-drawer-handle not found");
  }

  function onMouseDown(e: MouseEvent | TouchEvent) {
    // PC表示ならば、ドロワーは引っ張ることはできないので、なにもしない
    if (getCurrentDevice() === "PC") {
      return;
    }
    // クリックした場所が,`.drawer-content`クラスの要素の中ならば、
    // 通常スクロールとして、scrollStatusを"normal"にする
    if (
      e.target instanceof HTMLElement &&
      e.target.closest(".drawer-content") !== null
    ) {
      scrollStatus = "normal";
    } else {
      // それ以外の場所をクリックした場合は、ドロワーのハンドルをドラッグしていると
      // みなして、scrollStatusを"forced"にする
      scrollStatus = "forced";
    }
    if (e instanceof MouseEvent) {
      mouseStartPageY = e.pageY;
    } else if (e instanceof TouchEvent) {
      // タッチイベントの場合、最初のタッチ位置を取得
      mouseStartPageY = e.touches[0].pageY;
    }
    const spotDetailDrawer = document.getElementById("spot-detail-drawer");

    if (!spotDetailDrawer) {
      throw new Error("spot-detail-drawer not found");
    }

    const saleTicketDetailDrawer = document.getElementById(
      "sale-ticket-detail-drawer",
    );
    if (!saleTicketDetailDrawer) {
      throw new Error("sale-ticket-detail-drawer not found");
    }

    // マウス押下開始時の各ドロワーのtopの値を記録しておく
    const spotDetailDrawerComputedStyle =
      window.getComputedStyle(spotDetailDrawer);
    spotDetailDrawerInitialTop = parseInt(
      spotDetailDrawerComputedStyle.getPropertyValue("top").split("px")[0],
      10,
    );

    const saleTicketDetailDrawerComputedStyle = window.getComputedStyle(
      saleTicketDetailDrawer,
    );
    saleTicketDetailDrawerInitialTop = parseInt(
      saleTicketDetailDrawerComputedStyle
        .getPropertyValue("top")
        .split("px")[0],
      10,
    );
    // マウス押下開始時、各ドロワーのコンテンツのスクロール位置を記録しておく
    const spotDetailDrawerContent = document.getElementById(
      "spot-detail-drawer-content",
    );
    if (!spotDetailDrawerContent) {
      throw new Error("spot-detail-drawer-content not found");
    }
    const saleTicketDetailDrawerContent = document.getElementById(
      "sale-ticket-detail-drawer-content",
    );
    if (!saleTicketDetailDrawerContent) {
      throw new Error("sale-ticket-detail-drawer-content not found");
    }

    spotDetailDrawerContentInitialScrollTop = spotDetailDrawerContent.scrollTop;
    saleTicketDetailDrawerContentInitialScrollTop =
      saleTicketDetailDrawerContent.scrollTop;

    // 各ドロワーのtransitionを無効化する、してドラッグで滑らかに動くように
    spotDetailDrawer.style.transition = "";
    saleTicketDetailDrawer.style.transition = "";
  }
  spotDetailDrawer.addEventListener("mousedown", onMouseDown);
  spotDetailDrawer.addEventListener("touchstart", onMouseDown);
  saleTicketDetailDrawer.addEventListener("mousedown", onMouseDown);
  saleTicketDetailDrawer.addEventListener("touchstart", onMouseDown);

  function onMouseUp(e: MouseEvent | TouchEvent) {
    if (scrollStatus === "none") {
      return;
    }
    scrollStatus = "none";

    const currentMode = getMode();
    let top = 0;
    /** 慣性によるスクロール量の補正分 */
    const scrollInertiaCompensation = drawerMoveSpeed * 10;
    if (currentMode.mode === "singleSpot") {
      top =
        spotDetailDrawerInitialTop + mouseDiffPageY + scrollInertiaCompensation;
    } else if (currentMode.mode === "saleTicket") {
      top =
        saleTicketDetailDrawerInitialTop +
        mouseDiffPageY +
        scrollInertiaCompensation;
    } else {
      throw new Error("unexpected mode: " + currentMode.mode);
    }

    const spotDetailDrawer = document.getElementById("spot-detail-drawer");
    if (!spotDetailDrawer) {
      throw new Error("spot-detail-drawer not found");
    }
    const saleTicketDetailDrawer = document.getElementById(
      "sale-ticket-detail-drawer",
    );
    if (!saleTicketDetailDrawer) {
      throw new Error("sale-ticket-detail-drawer not found");
    }

    const spotDetailDrawerContent = document.getElementById(
      "spot-detail-drawer-content",
    );
    if (!spotDetailDrawerContent) {
      throw new Error("spot-detail-drawer-content not found");
    }
    const saleTicketDetailDrawerContent = document.getElementById(
      "sale-ticket-detail-drawer-content",
    );
    if (!saleTicketDetailDrawerContent) {
      throw new Error("sale-ticket-detail-drawer-content not found");
    }

    // topセット前に、transitionを設定して滑らかに動くようにする。
    spotDetailDrawer.style.transition = "top 0.3s ease";
    saleTicketDetailDrawer.style.transition = "top 0.3s ease";

    // しきい値は固定値ではなく、現在の表示領域から導出した動的値を使う。
    // これによりUI高さ変動後も「どこで最大化/初期化するか」の判定がずれない。
    if (top < getMobileDrawerThresholdTop()) {
      // ドロワーを最大表示する
      if (currentMode.mode === "singleSpot") {
        spotDetailDrawer.style.top = `${MOBILE_DRAWER_ENLARGED_TOP}px`;
        spotDetailDrawerContent.style.overflowY = "scroll";
      } else if (currentMode.mode === "saleTicket") {
        saleTicketDetailDrawer.style.top = `${MOBILE_DRAWER_ENLARGED_TOP}px`;
        saleTicketDetailDrawerContent.style.overflowY = "scroll";
      }
    } else {
      // ドロワーを初期位置に戻す
      if (currentMode.mode === "singleSpot") {
        // 初期位置を毎回再計算し、UI変動前の古いtop値へ戻ってしまうことを防ぐ。
        spotDetailDrawer.style.top = `${getMobileDrawerInitialTop()}px`;
        spotDetailDrawerContent.style.overflowY = "hidden";
      } else if (currentMode.mode === "saleTicket") {
        // saleTicket側も同様に、現在の実表示領域に合わせる。
        saleTicketDetailDrawer.style.top = `${getMobileDrawerInitialTop()}px`;
        saleTicketDetailDrawerContent.style.overflowY = "hidden";
      }
    }
  }
  window.document.addEventListener("mouseup", onMouseUp);
  window.document.addEventListener("touchend", onMouseUp);

  function onMouseMove(e: MouseEvent | TouchEvent) {
    if (scrollStatus === "none") {
      return;
    }
    if (e instanceof MouseEvent) {
      mouseDiffPageY = e.pageY - mouseStartPageY;
    } else if (e instanceof TouchEvent) {
      mouseDiffPageY = e.touches[0].pageY - mouseStartPageY;
    } else {
      throw new Error("unexpected event type");
    }

    const spotDetailDrawer = document.getElementById("spot-detail-drawer");
    if (!spotDetailDrawer) {
      throw new Error("spot-detail-drawer not found");
    }
    const spotDetailDrawerContent = document.getElementById(
      "spot-detail-drawer-content",
    );
    if (!spotDetailDrawerContent) {
      throw new Error("spot-detail-drawer-content not found");
    }
    const saleTicketDetailDrawer = document.getElementById(
      "sale-ticket-detail-drawer",
    );
    if (!saleTicketDetailDrawer) {
      throw new Error("sale-ticket-detail-drawer not found");
    }
    const saleTicketDetailDrawerContent = document.getElementById(
      "sale-ticket-detail-drawer-content",
    );
    if (!saleTicketDetailDrawerContent) {
      throw new Error("sale-ticket-detail-drawer-content not found");
    }

    const currentMode = getMode();
    if (currentMode.mode === "singleSpot") {
      let newTopValue = spotDetailDrawerInitialTop + mouseDiffPageY;
      // ドロワーは上に引き上げることはできない
      if (newTopValue < MOBILE_DRAWER_ENLARGED_TOP) {
        newTopValue = MOBILE_DRAWER_ENLARGED_TOP;
      }

      if (scrollStatus === "normal") {
        if (spotDetailDrawerContent.scrollTop !== 0) {
          return; // ドロワーのコンテンツがスクロールされている場合は、topを変更しない
        }
        // 現在のモードがsingleSpotの場合、スポット詳細ドロワーのtopを変更する
        spotDetailDrawer.style.top = `${
          newTopValue - spotDetailDrawerContentInitialScrollTop
        }px`;
      } else if (scrollStatus === "forced") {
        // ドロワーのハンドルをドラッグしている場合は、強制的にtopを変更する
        // コンテンツのscrollTopでの補償はしない
        spotDetailDrawer.style.top = `${newTopValue}px`;
      }

      // ドロワーの移動速度を計算する
      const currentDrawerTop = parseInt(
        spotDetailDrawer.style.top.replace("px", ""),
        10,
      );
      drawerMoveSpeed = currentDrawerTop - previousDrawerTop;
      previousDrawerTop = currentDrawerTop;

      // ドロワーのハンドルの位置を更新する
    } else if (currentMode.mode === "saleTicket") {
      let newTopValue = saleTicketDetailDrawerInitialTop + mouseDiffPageY;
      // ドロワーは上に引き上げることはできない
      if (newTopValue < MOBILE_DRAWER_ENLARGED_TOP) {
        newTopValue = MOBILE_DRAWER_ENLARGED_TOP;
      }
      if (scrollStatus === "normal") {
        if (saleTicketDetailDrawerContent.scrollTop !== 0) {
          return; // ドロワーのコンテンツがスクロールされている場合は、topを変更しない
        }
        // 現在のモードがsaleTicketの場合、販売チケット詳細ドロワーのtopを変更する
        saleTicketDetailDrawer.style.top = `${
          newTopValue - saleTicketDetailDrawerContentInitialScrollTop
        }px`;
      } else if (scrollStatus === "forced") {
        // ドロワーのハンドルをドラッグしている場合は、強制的にtopを変更する
        // コンテンツのscrollTopでの補償はしない
        saleTicketDetailDrawer.style.top = `${newTopValue}px`;
      }

      // ドロワーの移動速度を計算する
      const currentDrawerTop = parseInt(
        saleTicketDetailDrawer.style.top.replace("px", ""),
        10,
      );
      drawerMoveSpeed = currentDrawerTop - previousDrawerTop;
      previousDrawerTop = currentDrawerTop;
    }

    console.debug("drawerMoveSpeed:", drawerMoveSpeed);
  }
  window.document.addEventListener("mousemove", onMouseMove);
  window.document.addEventListener("touchmove", onMouseMove);
}

async function toggleDrawer(mode: Mode) {
  const location = window.location;
  const lang = location.pathname.split("/")[1];
  // singleSpotモードならば、スポット詳細ドロワーを開く
  if (mode.mode === "singleSpot") {
    const modeId = mode.id;
    if (modeId === null) {
      throw Error("unexpectedly mode.id is null");
    }
    const targetSpot = await getSpot(
      modeId.toString(),
      DATA_BLOCK.getEnv().API_URL,
      lang as Languages,
    );
    if (!targetSpot) {
      throw Error("spot not found");
    }
    await openSpotDetailDrawer(targetSpot);
  } else {
    // mode.mode !== "singleSpot"
    closeSpotDetailDrawer();
  }
  // saleTicketモードならば、販売チケット詳細ドロワーを開く
  if (mode.mode === "saleTicket") {
    const modeId = mode.id;
    if (modeId === null) {
      throw Error("unexpectedly mode.id is null");
    }
    const targetSaleTicket = await getSaleTicket(
      modeId.toString(),
      DATA_BLOCK.getEnv().API_URL,
      lang as Languages,
    );
    if (!targetSaleTicket) {
      throw Error("saleTicket not found");
    }
    await openSaleTicketDetailDrawer(targetSaleTicket);
  } else {
    // mode.mode !== "saleTicket"
    closeSaleTicketDetailDrawer();
  }
  closeFilterDrawer();
}
/** changeMode()をコールしたときに、ズームの挙動を制御するためのタイプ
 * - "normal": センタライズしてズームする
 * - "onlyCenterize": センタライズのみ行い、ズームはしない
 * - "noZoomAndCenterize": センタライズもズームもしない
 */
type ZoomType = "normal" | "onlyCenterize" | "noZoomAndCenterize";
async function changeMode(mode: Mode, zoomType: ZoomType = "normal") {
  showLoading();
  try {
    changeUrl(mode);
    const spots = await changeDrawedMarkers();
    changeUiVisibility(mode);
    if (zoomType !== "noZoomAndCenterize") {
      if (mode.mode === "singleSpot") {
        const centerSpot = spots.find((spot) => spot.topics_id === mode.id);
        if (!centerSpot) {
          throw new Error("centerSpot not found for mode: " + mode.mode);
        }
        zoomToSpots([centerSpot], zoomType === "onlyCenterize");
      } else {
        zoomToSpots(spots, zoomType === "onlyCenterize");
      }
    }
    await toggleDrawer(mode);
  } finally {
    hideLoading();
  }
}

function changeUrl(mode: Mode) {
  const url = new URL(document.location.href);
  // show-outdatedの値をURLに追加する
  if (mode.mode === "singleSpot") {
    const modeId = mode.id;
    if (modeId === null) {
      throw Error("unexpectedly mode.id is null");
    }
    url.searchParams.set("spot", modeId.toString());
    url.searchParams.delete("saleticket");
  } else if (mode.mode === "saleTicket") {
    const modeId = mode.id;
    if (modeId === null) {
      throw Error("unexpectedly mode.id is null");
    }
    url.searchParams.set("saleticket", modeId.toString());
    url.searchParams.delete("spot");
  } else {
    url.searchParams.delete("spot");
    url.searchParams.delete("saleticket");
  }
  window.history.pushState({}, "", url.toString());
}

/** AdvancedMarkernのcontentに渡すためのマーカーアイコン要素を作成する
 */
export function createMarkerElement(
  markerColor: string,
  iconImageUrlOrDuplication: string | number,
  labelText: string,
  emphasize: boolean = false,
): HTMLDivElement {
  const markerSize = emphasize ? EMPHASIZED_MARKER_SIZE : DEFAULT_MARKER_SIZE;
  // アイコンのベースとなるdiv
  // この下に、バックグラウンド画像とアイコン画像がぶら下がる
  const iconBaseElem = document.createElement("div");

  // バックグラウンド円形
  // based on : https://qiita.com/hal_sk/items/f31899d15a59c3e0e849
  const iconBackgroundElem = document.createElement("div");
  iconBackgroundElem.style.width = `0`;
  iconBackgroundElem.style.height = `0`;
  iconBaseElem.appendChild(iconBackgroundElem);

  const childSpan = document.createElement("span");
  if (emphasize) {
    childSpan.classList.add("override-border-color");
  }
  childSpan.style.display = "flex";
  childSpan.style.justifyContent = "center";
  childSpan.style.alignItems = "center";
  childSpan.style.boxSizing = "border-box";
  childSpan.style.width = `${markerSize.spotFrameSize}px`;
  childSpan.style.height = `${markerSize.spotFrameSize}px`;
  childSpan.style.color = "white";
  childSpan.style.background = "white";
  childSpan.style.boxShadow = "0 0 2px #000";
  childSpan.style.border = "solid 2px " + markerColor;
  childSpan.style.borderRadius = "0 70% 70%";
  childSpan.style.cursor = "pointer";
  childSpan.style.transformOrigin = "0 0";
  childSpan.style.transform = "rotateZ(-135deg)";
  if (emphasize) {
    childSpan.classList.add("override-background-color");
  }
  iconBackgroundElem.appendChild(childSpan);

  // アイコン画像
  // iconImageUrlOrDuplicationが文字列の場合は画像を表示
  if (typeof iconImageUrlOrDuplication === "string") {
    const iconImageElem = document.createElement("img");
    iconImageElem.src = iconImageUrlOrDuplication;
    iconImageElem.width = markerSize.imageSize;
    iconImageElem.height = markerSize.imageSize;
    iconImageElem.style.cursor = "pointer";
    iconImageElem.style.borderRadius = "50%";
    iconImageElem.style.position = "absolute";
    iconImageElem.style.bottom = `${markerSize.shiftImage}px`;
    iconImageElem.style.left = `-${markerSize.imageSize / 2}px`;
    iconBackgroundElem.appendChild(iconImageElem);
  }

  const labelElem = document.createElement("div");
  labelElem.style.position = "absolute";
  labelElem.style.top = `${markerSize.labelVerticalOffset}px`;
  // https://stackoverflow.com/questions/6626314/center-an-item-with-position-relative#:~:text=Much%20simpler%3A%20position%3A%20relative%3B%20left%3A%2050%25%3B%20transform%3A%20translateX%28-50%25%29%3B,parent%20element.%20You%20can%20do%20that%20vertically%20too.
  // こうやるとなぜか中央ぞろえができる
  labelElem.style.left = `50%`;
  labelElem.style.transform = "translateX(-50%)";
  labelElem.style.textAlign = "center";
  labelElem.style.fontSize = `${markerSize.labelFontSize}px`;
  labelElem.style.fontWeight = "500";
  labelElem.style.textShadow = "3px 3px 3px #FFF";
  labelElem.style.whiteSpace = "nowrap";
  labelElem.style.cursor = "pointer";
  labelElem.style.padding = "4px";
  labelElem.textContent = labelText;
  labelElem.style.fontFamily = "Noto Sans JP, sans-serif";
  iconBaseElem.appendChild(labelElem);

  // iconImageUrlOrDuplicationが数値の場合は、重複数を表示
  if (typeof iconImageUrlOrDuplication === "number") {
    const duplicationElem = document.createElement("div");
    duplicationElem.style.position = "absolute";
    duplicationElem.style.width = `${markerSize.imageSize}px`;
    duplicationElem.style.height = `${markerSize.imageSize}px`;
    duplicationElem.style.textAlign = "center";
    duplicationElem.style.top = `${markerSize.duplicateLabelTopOffset}px`;
    duplicationElem.style.right = `${markerSize.duplicateLabelRightOffset}px`;
    duplicationElem.style.transform = "translateX(-50%)";
    duplicationElem.style.fontSize = `${markerSize.duplicateLabelFontSize}px`;
    duplicationElem.style.color = "#000";
    duplicationElem.style.cursor = "pointer";
    duplicationElem.style.backgroundColor = markerColor;
    duplicationElem.style.borderRadius = "50%";
    duplicationElem.style.padding = "2px 4px";
    duplicationElem.innerText = `+${iconImageUrlOrDuplication}`;
    iconBaseElem.appendChild(duplicationElem);
  }

  return iconBaseElem;
}

/** 地図の3d/2d表示を切り替える */
function toggleMap3d2d() {
  if (!_map) {
    console.error("map is not initialized");
    return;
  }
  const from3dTo2dChanger = document.getElementById("3d-2d-changer");
  if (!from3dTo2dChanger) {
    throw new Error("3d-2d-changer not found");
  }
  if (_map.getPitch() === 0) {
    // 3D表示に切り替える
    _map.easeTo({
      pitch: 60,
      duration: 1000,
    });
    from3dTo2dChanger.innerText = "2D";
  } else {
    // 2D表示に切り替える
    _map.easeTo(
      {
        pitch: 0,
        bearing: 0,
      },
      { duration: 1000 },
    );
    from3dTo2dChanger.innerText = "3D";
  }
}

function showCurrentPos() {
  if (!_geolocate) {
    throw new Error("geolocate is not initialized");
  }

  _geolocate.trigger();
}

/** 現在ページを表示しているデバイスがPCが携帯機器かを取得する */
function getCurrentDevice(): "MOBILE" | "PC" {
  const windowWidth = window.innerWidth;
  if (windowWidth >= MOBILE_WIDTH) {
    return "PC";
  } else {
    return "MOBILE";
  }
}

/** 重複のあるSpot型配列をユニークにする */
function uniquifiesSpotArray(spots: Spot[]): Spot[] {
  const uniqueSpots: Spot[] = [];
  spots.forEach((spot) => {
    const topicsId = spot.topics_id;
    if (uniqueSpots.find((uniqueSpot) => uniqueSpot.topics_id === topicsId)) {
      // すでに存在するトピックIDの場合はスキップ
      return;
    }
    uniqueSpots.push(spot);
  });
  return uniqueSpots;
}

init();
